import React, { useEffect, useMemo, useState } from 'react';
import { TodoItem } from '../lib/todos';
import { getPreferredTimeZone, getGmtOffsetLabel, getHourInTimeZone, formatHourLabel } from '../utils/timezone';

type Props = {
    date: Date;
    items: TodoItem[];
    onAddTime: (hour: number) => void;
    onSelectEvent: (id: string) => void;
};

function ymdLocal(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export default function DayView({ date, items, onAddTime, onSelectEvent }: Props) {
    const hours = Array.from({ length: 24 }, (_, i) => i);

    // Preferred timezone
    const [timeZone, setTimeZone] = useState<string>(() => getPreferredTimeZone());
    useEffect(() => {
        const onTz = (e: any) => setTimeZone(e?.detail?.timeZone || getPreferredTimeZone());
        window.addEventListener('gv:timezoneChanged' as any, onTz);
        return () => window.removeEventListener('gv:timezoneChanged' as any, onTz);
    }, []);

    // Day key string e.g. 2025-08-16
    const dayKey = useMemo(() => ymdLocal(date), [date]);

    // Events for this day
    const events = useMemo(() => {
        return items.filter(it => it.due_date && it.due_date.startsWith(dayKey));
    }, [items, dayKey]);

    // Current time (hour highlight; updates hourly)
    const [now, setNow] = useState<Date>(new Date());
    useEffect(() => {
        // Snap updates on the hour mark
        const tick = () => setNow(new Date());
        const msToNextHour = (() => {
            const d = new Date();
            return (60 - d.getMinutes()) * 60000 - d.getSeconds() * 1000 - d.getMilliseconds();
        })();
        const timeout = setTimeout(() => {
            tick();
            // After first snap, update every hour
            const hourly = setInterval(tick, 60 * 60 * 1000);
            (window as any).__dayViewHourly = hourly;
        }, Math.max(0, msToNextHour));
        return () => {
            clearTimeout(timeout);
            const hourly = (window as any).__dayViewHourly; if (hourly) clearInterval(hourly);
        };
    }, []);
    const isToday = useMemo(() => {
        const t = new Date();
        return t.getFullYear() === date.getFullYear() && t.getMonth() === date.getMonth() && t.getDate() === date.getDate();
    }, [date]);

    const tzLabel = useMemo(() => {
        return getGmtOffsetLabel(timeZone);
    }, [timeZone]);

    return (
        <div className="w-full bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
            {/* No internal day header; global header shows date */}
            <div className="flex">
                {/* Time Column */}
                <div className="w-16 flex-shrink-0 border-r border-gray-800">
                    <div className="h-12 border-b border-gray-800 flex items-center justify-center text-[10px] text-gray-400">
                        {tzLabel}
                    </div>
                    {hours.map(hour => (
                        <div key={hour} className="h-[60px] border-b border-gray-800 text-xs text-gray-400 p-1">
                            {formatHourLabel(hour, timeZone, true)}
                        </div>
                    ))}
                </div>

                {/* Grid */}
                <div className="flex-1 relative">
                    {hours.map(hour => (
                        <div key={hour} className="h-[60px] border-b border-gray-800 hover:bg-gray-800/30 cursor-pointer group" onClick={() => onAddTime(hour)}>
                            <div className="opacity-0 group-hover:opacity-100 p-1 text-xs text-gray-400">+ add at {formatHourLabel(hour, timeZone, true)}</div>
                        </div>
                    ))}

                    {/* Current time hour highlight (snaps to hour; no moving dot/line). On mobile, remove left border line. */}
                    {isToday && (
                        <div
                            className="absolute left-0 right-0 bg-blue-500/10 md:border-l-2 md:border-blue-500 z-5 pointer-events-none"
                            style={{ top: `${getHourInTimeZone(now, timeZone) * 60}px`, height: '60px' }}
                        />
                    )}

                    {/* Events */}
                    {events.map(ev => {
                        const isAllDay = ev.due_date?.endsWith('T00:00:00.000Z');
                        const top = isAllDay ? 0 : new Date(ev.due_date!).getHours() * 60 + new Date(ev.due_date!).getMinutes();
                        const height = isAllDay ? 40 : 60;
                        const time = isAllDay ? 'All day' : new Date(ev.due_date!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        return (
                            <div key={ev.id} className="absolute left-1 right-1 bg-blue-600 border border-blue-500 rounded text-white text-xs p-1 cursor-pointer hover:bg-blue-500 z-10" style={{ top, height: Math.max(height, 30) }} onClick={() => onSelectEvent(ev.id)}>
                                <div className="font-medium truncate">{ev.title}</div>
                                <div className="text-[10px] opacity-75">{time}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
